/**
 * kanban/kanbanUtils.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Pure utility functions — no React, no side effects.
 * Kept separate so they can be unit-tested in isolation.
 */

import { BASE_URL } from '@/config/serverApiConfig';

// ── Task grouping ─────────────────────────────────────────────────────────────

/**
 * Groups a flat task array into a Map<statusKey, task[]>.
 * Columns not present in the task list will still get an empty array
 * if their key is passed in `columnKeys`.
 *
 * @param {object[]} tasks
 * @param {string[]} columnKeys   - ordered list of status keys (from KANBAN_COLUMNS)
 * @returns {Map<string, object[]>}
 */
export function groupTasksByStatus(tasks, columnKeys) {
    const map = new Map(columnKeys.map((k) => [k, []]));
    tasks.forEach((task) => {
        const col = map.get(task.status);
        if (col) col.push(task);
        // Tasks with unknown statuses are silently dropped from the board
        // (they still exist in the list view and database).
    });
    return map;
}

// ── Time helpers ──────────────────────────────────────────────────────────────

/**
 * Parses a human-readable estimated time string (e.g. "4h", "1h 30m", "45m")
 * into milliseconds.
 *
 * @param {string} str
 * @returns {number} milliseconds
 */
export function parseEstimatedToMs(str) {
    if (!str) return 0;
    let ms = 0;
    const h = str.match(/(\d+(?:\.\d+)?)h/);
    const m = str.match(/(\d+)m/);
    if (h) ms += parseFloat(h[1]) * 3_600_000;
    if (m) ms += parseInt(m[1]) * 60_000;
    return ms;
}

/**
 * Formats milliseconds into a short human-readable string.
 * e.g. 5400000 → "1h 30m",  3600000 → "1h",  0 → "0h"
 *
 * @param {number} ms
 * @returns {string}
 */
export function formatMs(ms) {
    if (!ms || ms <= 0) return '0h';
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// ── Name / avatar helpers ─────────────────────────────────────────────────────

/**
 * Returns up-to-2-character initials from a full name.
 * "Jane Doe" → "JD",  "Alice" → "AL" (first two chars)
 *
 * @param {string} name
 * @returns {string}
 */
export function getInitials(name = '') {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
}

/**
 * Builds a display name from an Admin/Employee object whose fields may be
 * split across `name` and `surname`.
 *
 * @param {object|null} person
 * @returns {string}
 */
export function getFullName(person) {
    if (!person) return '';
    return [person.name, person.surname].filter(Boolean).join(' ').trim() || person.name || '';
}

/**
 * Resolves a photo filename to a fully-qualified URL.
 * Bare filenames are stored in the Admin model; this prefixes BASE_URL so
 * the browser doesn't request them from the React dev server.
 *
 * @param {string|null} photo
 * @returns {string|null}
 */
export function getPhotoUrl(photo) {
    if (!photo) return null;
    if (/^https?:\/\//i.test(photo)) return photo;                         // already absolute
    const relative = photo.startsWith('/') ? photo : `/public/uploads/admin/${photo}`;
    const base = (BASE_URL || '').replace(/\/$/, '');
    return `${base}${relative}`;
}

// ── Due-date helpers ──────────────────────────────────────────────────────────

/**
 * Returns true when a task is overdue (due date is in the past and the task
 * is not yet Completed or Cancelled).
 *
 * @param {object} task
 * @returns {boolean}
 */
export function isOverdue(task) {
    if (!task.dueDate) return false;
    if (['Completed', 'Cancelled'].includes(task.status)) return false;
    return new Date(task.dueDate) < new Date();
}
